import dayjs from 'dayjs';

type DateTime = string | Date;
type Unit = 'date' | 'time';
type DateCombinationResult = string | null;
type StringAnyRecord = Record<string, any>;

type MerchantProfileItem = {
    key: string;
    value: string;
};

export const dateFormat = (date: DateTime, format?: string) => {
    if (!date) return '';
    return dayjs(date).format(format || 'MM/DD/YYYY');
};
export const timeFormat = (date: DateTime, format?: string) => {
    if (!date) return '';
    return dayjs(date).format(format || 'hh:mm:ss A');
};
export const convertToISOString = (date: DateTime): string => {
    if (isNaN(dayjs(date).valueOf())) {
        return dayjs().toISOString();
    }
    return dayjs(date).toISOString();
};

export const convertToDateObject = (date: DateTime): Date => {
    if (isNaN(dayjs(date).valueOf())) {
        return new Date();
    }
    return dayjs(date).toDate();
};

// compare with current date if second argument is omitted
export const isDateBefore = (date1: DateTime, date2?: DateTime) => {
    const isoDate1 = convertToISOString(date1);
    const isoDate2 = convertToISOString(date2 || '');
    if (!isoDate1 || !isoDate2) return null;
    return dayjs(isoDate1).isBefore(isoDate2);
};

// compare with current date if second argument is omitted
export const isDateAfter = (date1: DateTime, date2?: DateTime) => {
    const isoDate1 = convertToISOString(date1);
    const isoDate2 = convertToISOString(date2 || '');
    if (!isoDate1) return null;
    return dayjs(isoDate1).isAfter(isoDate2);
};

const combineDateTime = (orgDateTime: DateTime, newDateTime: DateTime, unit: Unit): DateCombinationResult => {
    // if(!orgDateTime &&  !newDateTime) return null;
    try {
        const orgDateTimeObj = orgDateTime instanceof Date ? orgDateTime : new Date(orgDateTime);
        if (!dayjs(orgDateTimeObj).isValid()) {
            const newDateAndTimeString = dayjs(newDateTime).toISOString();
            return newDateAndTimeString ? newDateAndTimeString : null;
        }
        const newDateTimeObj = newDateTime instanceof Date ? newDateTime : new Date(newDateTime);
        // if(!newDateTimeObj) return null;
        const orgDate = dayjs(orgDateTimeObj).format('YYYY-MM-DD');
        const newDate = dayjs(newDateTimeObj).format('YYYY-MM-DD');
        const orgTime = dayjs(orgDateTimeObj).format('HH:mm:ss');
        const newTime = dayjs(newDateTimeObj).format('HH:mm:ss');
        let combinedDateTime: string | Date = orgDateTimeObj;
        if (unit === 'date') {
            combinedDateTime = `${newDate}T${orgTime}`;
        } else {
            combinedDateTime = `${orgDate}T${newTime}`;
        }

        return dayjs(combinedDateTime).toISOString();
    } catch (e) {
        return '';
    }
};

export const combineDate = (originalDate: DateTime, newDate: DateTime): DateCombinationResult => {
    return combineDateTime(originalDate, newDate, 'date');
};

export const combineTime = (originalTime: DateTime, newTime: DateTime): DateCombinationResult => {
    return combineDateTime(originalTime, newTime, 'time');
};

export const getUpdatedValues = (obj1: StringAnyRecord, obj2: StringAnyRecord, keepUnmatchedKeys: boolean = false): StringAnyRecord => {
    const updatedValues: Record<string, any> = {};

    for (const key in obj2) {
        if (obj1.hasOwnProperty(key) && obj1[key] !== obj2[key]) {
            updatedValues[key] = obj2[key];
        } else if (!obj1.hasOwnProperty(key)) {
            updatedValues[key] = obj2[key];
        }
    }

    if (keepUnmatchedKeys) {
        for (const key in obj1) {
            if (!obj2.hasOwnProperty(key)) {
                updatedValues[key] = obj1[key];
            }
        }
    }

    return updatedValues;
};

export const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

const format = (value: number) => {
    return '$' + value.toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
};

export const formatAmount = (value: string | number = 0, amountType: 'dollar' | 'cents' = 'dollar') => {
    if (!value) return format(0);
    let vText = String(value).replace('$', '').replace(',', '');

    let vValue = 0.0;
    if (vText) {
        vValue = +vText;
    }
    if (amountType === 'cents') {
        vValue = vValue / 100;
    }
    return format(vValue);
};

export const getFullName = (user: { firstName: string; lastName: string }) => {
    const { firstName, lastName } = user || {};
    if (!firstName && !lastName) return '';
    return `${firstName || ''} ${lastName || ''}`.trim();
};

export const getDateAndTime = (date: string) => {
    if (!date || !dayjs(date).isValid()) return '';
    return `${dateFormat(date) || ''} ${timeFormat(date) || ''}`.trim();
};

export const getAddress = (address?: { street1: string; street2: string } | null) => {
    if (!address) return '';
    const { street1, street2 } = address || {};
    return `${street1 || ''} ${street2 || ''}`.trim();
};

export const debounce = (callback: any, wait: number) => {
    let timeoutId: any = null;
    return (...args: any) => {
        window.clearTimeout(timeoutId);
        timeoutId = window.setTimeout(() => {
            callback(...args);
        }, wait);
    };
};

export const extractErrorMessage = (backendString: string): string | null => {
    if (!backendString) return '';
    const jsonMatch: RegExpMatchArray | null = backendString.match(/\{.*\}/);

    if (jsonMatch) {
        const jsonString: string = jsonMatch[0];

        try {
            const errorData: any = JSON.parse(jsonString);

            let errorMessage: string = errorData?.message || errorData?.error;
            if (errorData?.additionalStatusCode === 'INSUFFICIENT_BALANCE') {
                errorMessage = 'Insufficient Balance';
            }

            return errorMessage;
        } catch (error) {
            console.error('Error parsing JSON:', error);
            return null;
        }
    } else {
        console.log('No JSON part found in the string.');
        return backendString;
    }
};

export const fetchAndPrepareImage = async (imageUrl: string) => {
    try {
        const response = await fetch(imageUrl);

        // Convert the fetched data into a Blob
        const blob = await response.blob();

        // Create a File object from the Blob
        const file = new File([blob], 'image.png', { type: 'image/png' });
        return file;
    } catch (error) {
        console.error('Error fetching image:', error);
        return null;
    }
};
export const getTodayFilter = () => {
    const startDate = dayjs().startOf('day');
    const endDate = dayjs().endOf('day');
    return {
        dayFrom: startDate.format(),
        dayTo: endDate.format(),
        value: 'today',
    };
};

export const sortByField = (arr: Array<any>, sortByKey: string, order: 'ASC' | 'DESC' = 'ASC'): Array<any> => {
    if (!sortByKey) {
        return arr;
    }
    return arr.sort((a, b) => {
        let _a = get(a, sortByKey, '');
        let _b = get(b, sortByKey, '');
        if (order === 'ASC') {
            return _a > _b ? 1 : _b > _a ? -1 : 0;
        }
        return _b > _a ? 1 : _a > _b ? -1 : 0;
    });
};

/**
 * Finds values in an array of objects based on provided keys.
 * @param {Array} profileItems - The array of objects containing key value pair to search through.
 * @param {Array} keysToFind - The keys to search for in the array objects. It would be an array of strings
 * @returns {Object} - Object containing key-value pairs found in the array.
 */
export const findValuesByKeys = (profileItems: MerchantProfileItem[], keysToFind: Array<string>): object => {
    // checking if provided profile items are json and parsing it so if it has the array and if not then return the empty object
    if (typeof profileItems === 'string') {
        try {
            profileItems = JSON.parse(profileItems);
            if (!Array.isArray(profileItems)) {
                return {};
            }
        } catch (e) {
            return {};
        }
    }
    if (!profileItems?.length || !keysToFind?.length) {
        return {};
    }
    const uniqKeys = [...new Set(keysToFind)];
    if (!uniqKeys?.length) {
        return {};
    }
    return profileItems.reduce((result: any, item) => {
        if (keysToFind.includes(item.key)) {
            result[item.key] = item.value;
        }
        return result;
    }, {});
};

export const createQueryPayams = (obj: { [key: string]: any }): string => {
    const queryParams: string[] = [];
    for (let key in obj) {
        if (obj.hasOwnProperty(key)) {
            queryParams.push(encodeURIComponent(key) + '=' + encodeURIComponent(obj[key]));
        }
    }
    return queryParams.join('&');
};

const _get = (object: any, fieldPath: string) => {
    const path = _getPath(fieldPath);
    let pivot = object;
    while (path.length > 0) {
        if (!pivot) {
            return path.length > 0 ? undefined : pivot;
        }
        pivot = pivot[path.shift()];
    }
    return pivot;
};

/**
 * Gets a property value. This method is null safe.
 *
 * @param object the object to that contains the value.
 * @param fieldPath the field's path.
 * @param defaultValue the default value.
 * @return the value
 */
export const get = (object: any, fieldPath: string, defaultValue: any) => {
    const value = _get(object, fieldPath);
    if (defaultValue === undefined) {
        return value;
    }
    return isDefined(value) ? value : defaultValue;
};

const isDefined = (value?: any) => value !== null && value !== undefined;

/**
 * @private
 * Used internally by the set method.
 */
const _getPath = (accessorString: string): any => {
    let path: any = [];
    const split = accessorString.split('.');
    split.forEach((field) => {
        const match = field.match(/\[\d+\]/g);
        if (!match) {
            path.push(field);
        } else if (!field.endsWith(']')) {
            throw new Error(`Invalid field name ${field}`);
        } else {
            let name = field;
            const indexes: any = [];
            match.forEach((chunk) => {
                name = name.replace(chunk, '');
                const num = chunk.match(/\d+/);
                indexes.push(Number(num && num[0]));
            });
            if (name.length > 0) {
                path = [...path, name, ...indexes];
            } else {
                path = [...path, ...indexes];
            }
        }
    });
    return path;
};

export function deletePropertyPath(obj: any, path: any) {
    if (!obj || typeof obj !== 'object' || typeof path !== 'string' || path.length === 0) {
        return;
    }

    const keys = path.split('.'); // Split the path by dot
    let current = obj;

    // Iterate through the keys, stopping before the last key
    for (let i = 0; i < keys.length - 1; i++) {
        if (current[keys[i]] === undefined) {
            return; // Path does not exist
        }
        current = current[keys[i]];
    }

    // Check the last key's value before deleting
    const lastKey = keys[keys.length - 1];
    if (current.hasOwnProperty(lastKey) && !current[lastKey]) {
        delete current[lastKey];
    }
}

export const getInitials = (name: string) => {
    const namesArray = name.trim().split(' ');
    if (namesArray.length > 1) {
        const firstInitial = namesArray[0][0];
        const lastInitial = namesArray[namesArray.length - 1][0];
        return (firstInitial + lastInitial).toUpperCase();
    } else {
        return namesArray[0][0].toUpperCase();
    }
};
export const isValidCurrencyString = (str: string) => {
    const validPattern = /^\$?[0-9]*$/;
    return validPattern.test(str);
};

export const isInvoiceReceiver = (role: string) => role === 'RECIPIENT';
